/**
 * The product's one smooth-scroll engine.
 *
 * Lenis works by swallowing wheel events and driving `window.scrollTo` on every
 * animation frame. Two instances therefore do not coexist — they fight: each
 * frame, both write a scroll position to the same document, and the result is a
 * scroll that stalls, jumps, or ignores the wheel outright.
 *
 * That is what the product was doing. Four places created an instance
 * (`lenis-scroll-provider`, `homepage/use-scroll-effects`,
 * `gift-list-search/use-public-search-motion`, `host-panel/shell/panel-layers`),
 * each with its own `requestAnimationFrame` loop, and only the first registered
 * itself on `window.__tbLenis` or guarded against duplicates. Worse, that one is
 * the one that never destroyed itself: "unmounting does not destroy it" was the
 * rule, so after a single visit to a PDP its loop ran for the rest of the
 * session — and the next home or `/listas` added a second engine on top of it.
 *
 * Measured on the test host (2026-10-19): the home scrolled 3240px for twelve
 * wheel ticks and `/listas` 154px, with `window.__tbLenis` absent on both even
 * though `<html>` carried Lenis's own classes. Reported as Clarity recording the
 * visitor as motionless on the home and on `/listas`, working on a list's page,
 * then working on `/listas` only after coming back from one — which is the shape
 * of "whichever engine happens to be alive wins".
 *
 * So the engine lives here, once, and callers take a reference:
 *
 * ```ts
 * const lenis = await acquireLenis(MY_OPTIONS);   // creates it, or shares it
 * …
 * releaseLenis();                                 // last one out destroys it
 * ```
 *
 * **Options come from whoever creates it.** They are not merged: the home asks
 * for `syncTouch` and nothing else does, and quietly applying one page's feel to
 * every other page is a second way to be inconsistent. A page that mounts while
 * another still holds the engine shares what is already running; once the
 * previous page releases, the next acquirer builds it again with its own.
 *
 * `window.__tbLenis` stays as the published handle, because the page and screen
 * transitions ask the engine to jump to the top through it
 * (`page-transition.ts`, `screen-transition.tsx`). Those calls used to be no-ops
 * anywhere but a PDP; with one engine they work everywhere.
 */

/** Instance type of the dynamically imported Lenis default export. */
type LenisInstance = InstanceType<typeof import("lenis").default>;

/** What `new Lenis(...)` accepts. Narrow on purpose: only what callers pass. */
export type LenisEngineOptions = {
  readonly lerp?: number;
  readonly duration?: number;
  readonly smoothWheel?: boolean;
  readonly wheelMultiplier?: number;
  readonly syncTouch?: boolean;
};

/**
 * The feel the whole product scrolls with (Motion Guide; PDP Req. 14.1).
 *
 * Three of the four callers declared exactly these numbers, one of them as
 * literals. The home adds `syncTouch` to this and is the only one that does.
 */
export const LENIS_ENGINE_OPTIONS: LenisEngineOptions = {
  lerp: 0.08,
  duration: 1.5,
  smoothWheel: true,
  wheelMultiplier: 1.2,
};

/** The single instance, or `null` when nobody holds one. */
let engine: LenisInstance | null = null;
/** How many mounted callers are holding {@link engine}. */
let holders = 0;
/** The one frame loop driving {@link engine}. */
let frame: number | null = null;
/**
 * Guards the gap between `acquireLenis` and the dynamic import resolving: two
 * callers mounting in the same tick must not each construct an instance.
 */
let pending: Promise<LenisInstance | null> | null = null;

declare global {
  interface Window {
    /**
     * The product's smooth-scroll engine while one is held, published for the
     * page and screen transitions. Owned by `lib/motion/lenis-engine.ts`; never
     * assign it from anywhere else.
     */
    __tbLenis?: LenisInstance;
  }
}

function startFrameLoop(instance: LenisInstance): void {
  if (frame !== null) return;

  const tick = (time: number) => {
    instance.raf(time);
    frame = window.requestAnimationFrame(tick);
  };
  frame = window.requestAnimationFrame(tick);
}

/**
 * Takes a reference to the engine, creating it when there is none.
 *
 * Resolves `null` when Lenis cannot be loaded — it is progressive enhancement,
 * and every caller already has a native fallback. A caller that gets `null` must
 * still {@link releaseLenis}, so the count stays honest.
 */
export async function acquireLenis(
  options: LenisEngineOptions = LENIS_ENGINE_OPTIONS,
): Promise<LenisInstance | null> {
  holders += 1;

  if (engine !== null) {
    startFrameLoop(engine);
    return engine;
  }

  if (pending !== null) return pending;

  pending = import("lenis")
    .then(({ default: Lenis }) => {
      // Everyone may have released while the import was in flight; building an
      // engine nobody holds would leave a loop running over a dead reference.
      if (holders === 0) return null;

      const instance = new Lenis({ ...options });
      engine = instance;
      window.__tbLenis = instance;
      startFrameLoop(instance);
      return instance;
    })
    .catch(() => null)
    .finally(() => {
      pending = null;
    });

  return pending;
}

/**
 * Gives a reference back. The last one out stops the loop and destroys the
 * engine, which is what hands the document's scroll back to the browser.
 */
export function releaseLenis(): void {
  holders = Math.max(0, holders - 1);
  if (holders > 0) return;

  if (frame !== null) {
    window.cancelAnimationFrame(frame);
    frame = null;
  }

  engine?.destroy();
  engine = null;
  delete window.__tbLenis;
}

/** The engine right now, without taking a reference. `null` when none is held. */
export function currentLenis(): LenisInstance | null {
  return engine ?? window.__tbLenis ?? null;
}

/** Test seam: forgets the engine without destroying it. Never call in app code. */
export function __resetLenisEngineForTests(): void {
  if (frame !== null) window.cancelAnimationFrame(frame);
  frame = null;
  engine = null;
  holders = 0;
  pending = null;
  delete window.__tbLenis;
}
